import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import SearchSection from './SearchSection';
import StudentTable from './StudentTable';
import ChargeForm from './ChargeForm';
import PendingChargesSection from './PendingChargesSection';
import LoaderOverlay from '../../components/LoaderOverlay';
import styles from '../../styles/GeneracionCargosManualesStyles/GeneracionCargosManuales.module.css';
import { getEstudiantesCampus, getCampusDropdown } from '../../api/cargosManuales';

function GeneracionCargosManualesScreen() {
  // Estado global para recargos por id
  const [recargosById, setRecargosById] = useState({});
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [campus, setCampus] = useState(() => localStorage.getItem('selectedCampus') || '');
  const [students, setStudents] = useState([]);
  const [campusIdState, setCampusIdState] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 8; // server page size
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sincronizar campus con HeaderGlobal (localStorage) y recargar estudiantes cuando cambie
  // Sincronizar campus global (nombre) con su ID antes de cargar estudiantes
  useEffect(() => {
    const handleStorageChange = () => {
      const newCampus = localStorage.getItem('selectedCampus') || '';
      setCampus(newCampus);
    };
    // Nuevo: escuchar evento personalizado para cambio inmediato
    const handleCampusChanged = (e) => {
      const newCampus = e.detail?.campus || localStorage.getItem('selectedCampus') || '';
      setCampus(newCampus);
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('campusChanged', handleCampusChanged);

    // Inicial: cargar estudiantes para el campus actual
    const fetchAndLoad = async () => {
      const currentCampusName = localStorage.getItem('selectedCampus') || '';
      setCampus(currentCampusName);
      if (currentCampusName) {
        const campusList = await getCampusDropdown();
        if (campusList && Array.isArray(campusList)) {
          localStorage.setItem('campusDropdownCache', JSON.stringify(campusList));
        }
        const campusObj = campusList.find(c => c.nombre === currentCampusName || c.nombre_campus === currentCampusName);
        if (campusObj && campusObj.id) {
          setCampusIdState(campusObj.id);
          loadStudents(campusObj.id, searchTerm, 1);
        } else {
          setStudents([]);
        }
      } else {
        setStudents([]);
      }
    };
    fetchAndLoad();
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('campusChanged', handleCampusChanged);
    };
    // eslint-disable-next-line
  }, []);

  // Cuando cambie el campus, recargar estudiantes
  useEffect(() => {
    const fetchAndLoad = async () => {
      if (campus) {
        const campusList = await getCampusDropdown();
        if (campusList && Array.isArray(campusList)) {
          localStorage.setItem('campusDropdownCache', JSON.stringify(campusList));
        }
        const campusObj = campusList.find(c => c.nombre === campus || c.nombre_campus === campus);
        if (campusObj && campusObj.id) {
          setCampusIdState(campusObj.id);
          loadStudents(campusObj.id, searchTerm, 1);
        } else {
          setStudents([]);
        }
      } else {
        setStudents([]);
      }
    };
    fetchAndLoad();
    // eslint-disable-next-line
  }, [campus]);

  const loadStudents = async (campusId, search = '', pageToLoad = 1, showLoader = true) => {
    const doShow = !!showLoader;
    if (doShow) setLoading(true);
    setError(null);
    try {
      let response;
      if (!campusId || campusId === 'all' || campusId === 'Todos' || campusId === 'Todos los Campus') {
        response = await getEstudiantesCampus('all', '', pageToLoad, pageSize);
      } else {
        response = await getEstudiantesCampus(campusId, search, pageToLoad, pageSize);
      }
      if (response.success) {
        const pageStudents = response.estudiantes || [];
        setStudents(pageStudents);
        setTotalCount(response.count || pageStudents.length);
        setPage(response.page || pageToLoad);
        setTotalPages(response.total_pages || Math.max(1, Math.ceil((response.count || pageStudents.length) / pageSize)));
      } else {
        setError('No se pudieron cargar los estudiantes');
        setStudents([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error al cargar estudiantes:', err);
      setError('Error al buscar estudiantes. Intente nuevamente.');
      setStudents([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      if (doShow) setLoading(false);
    }
  };



  // El filtrado se realiza en el backend: al buscar, pedir la página 1 con el término
  const handleSearch = async () => {
    if (campusIdState) {
      await loadStudents(campusIdState, searchTerm, 1);
    }
  };

  // Búsqueda en tiempo real: debounce para llamar al backend mientras escribe
  useEffect(() => {
    const handler = setTimeout(() => {
      if (campusIdState) {
        // Don't show the full-screen loader for live typing to avoid stealing focus
        loadStudents(campusIdState, searchTerm, 1, false);
      }
    }, 400);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, campusIdState]);

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
  };

  const handleBackToSearch = async () => {
    setSelectedStudent(null);
    setError(null);
    // Obtener el ID real del campus antes de recargar estudiantes
    if (campus) {
      const campusList = await getCampusDropdown();
      if (campusList && Array.isArray(campusList)) {
        localStorage.setItem('campusDropdownCache', JSON.stringify(campusList));
      }
      const campusObj = campusList.find(c => c.nombre === campus || c.nombre_campus === campus || c.id === campus);
      if (campusObj && campusObj.id) {
        loadStudents(campusObj.id, searchTerm);
      } else {
        setStudents([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } else {
      setStudents([]);
      setTotalCount(0);
      setTotalPages(1);
    }
  };

  return (
    <div className={styles.layout}>
      <div className={styles.sidebarColumn}>
        <Sidebar />
      </div>
      <div className={styles.contentColumn}>
        <div className={styles.contentArea}>
          <HeaderGlobal title="Generación de Cargos Manuales" />
          <div className={styles.contentWrapper} style={{ position: 'relative', minHeight: 'calc(100vh - 64px)', height: 'auto' }}>
            <LoaderOverlay visible={loading} label="Cargando estudiantes..."/>
            {!selectedStudent ? (
              <div>
                <SearchSection
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  onSearch={handleSearch}
                  loading={loading}
                />
                {error && (
                  <div className={styles.errorMessage}>{error}</div>
                )}
                <StudentTable 
                  students={students}
                  onSelectStudent={handleSelectStudent}
                  serverPaging={true}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={(p) => {
                    if (campusIdState) loadStudents(campusIdState, searchTerm, p);
                  }}
                />
              </div>
            ) : (
              <ChargeForm 
                student={selectedStudent}
                campusId={(() => {
                  if (!campus) return '';
                  const campusList = JSON.parse(localStorage.getItem('campusDropdownCache') || '[]');
                  const campusObj = campusList.find(c => c.nombre === campus || c.nombre_campus === campus || c.id === campus);
                  return campusObj && campusObj.id ? campusObj.id : campus;
                })()}
                onBack={handleBackToSearch}
                recargoCalculado={window.__lastDisplayRecargos || 0}
                recargosById={recargosById}
              />
            )}
                {/* Render PendingChargesSection oculto solo para sincronizar recargosById si es necesario, o pásalo donde corresponda */}
                {/*
                <PendingChargesSection
                  ...props
                  onRecargosByIdChange={setRecargosById}
                />
                */}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GeneracionCargosManualesScreen;
